/**
 * mermaid SVG 의 가독성·이미지 복사 유틸.
 *
 * mermaid 는 `useMaxWidth:true` 일 때 SVG 에 `style="max-width:Npx"` + `width="100%"` 를 넣어
 * 컨테이너 폭에 맞춰 축소시킨다. 폭 넓은 gantt 등은 이 때문에 글자가 작아져 안 읽힌다.
 * `normalizeMermaidSvg` 가 이를 viewBox 기준 명시적 px 크기로 되돌려 자연 크기를 보장한다.
 */

/** SVG 문자열에서 viewBox 의 [width, height] 를 추출. 없으면 width/height 속성 폴백. */
function readSvgSize(svg: string): { width: number; height: number } | null {
  const viewBox = svg.match(
    /viewBox\s*=\s*["']\s*([\d.+-]+)\s+([\d.+-]+)\s+([\d.+-]+)\s+([\d.+-]+)\s*["']/i,
  );
  if (viewBox) {
    const w = Number.parseFloat(viewBox[3] ?? '');
    const h = Number.parseFloat(viewBox[4] ?? '');
    if (w > 0 && h > 0) return { width: w, height: h };
  }
  const wAttr = svg.match(/\bwidth\s*=\s*["']\s*([\d.]+)(?:px)?\s*["']/i);
  const hAttr = svg.match(/\bheight\s*=\s*["']\s*([\d.]+)(?:px)?\s*["']/i);
  if (wAttr && hAttr) {
    const w = Number.parseFloat(wAttr[1] ?? '');
    const h = Number.parseFloat(hAttr[1] ?? '');
    if (w > 0 && h > 0) return { width: w, height: h };
  }
  return null;
}

/**
 * mermaid 출력 SVG 를 자연 크기(viewBox px)로 정규화한다.
 * - 루트 `<svg ...>` 의 `style` 에서 `max-width` 선언 제거
 * - `width="100%"`(또는 %) → viewBox 의 px width 로 치환, height 도 명시
 * 결과적으로 어떤 다이어그램 타입이든 컨테이너 축소 없이 원래 크기로 그려진다.
 */
export function normalizeMermaidSvg(svg: string): string {
  const size = readSvgSize(svg);
  return svg.replace(/<svg\b([^>]*)>/i, (_full, attrsRaw: string) => {
    let attrs = attrsRaw;
    // style 안의 max-width 선언만 제거 (다른 style 보존).
    attrs = attrs.replace(/style\s*=\s*"([^"]*)"/i, (_m, body: string) => {
      const cleaned = body
        .split(';')
        .map((s) => s.trim())
        .filter((s) => s && !/^max-width\s*:/i.test(s))
        .join('; ');
      return cleaned ? `style="${cleaned}"` : '';
    });
    // width/height 속성을 명시 px 로.
    attrs = attrs.replace(/\swidth\s*=\s*"[^"]*"/i, '').replace(/\sheight\s*=\s*"[^"]*"/i, '');
    if (size) {
      attrs += ` width="${size.width}" height="${size.height}"`;
    }
    return `<svg${attrs}>`;
  });
}

/** `<html>` 의 CSS 변수 토큰을 hsl() 문자열로 읽는다. 없으면 폴백. */
export function readThemeSurface(): string {
  if (typeof window === 'undefined') return '#ffffff';
  const style = getComputedStyle(document.documentElement);
  const raw =
    style.getPropertyValue('--surface').trim() || style.getPropertyValue('--background').trim();
  return raw ? `hsl(${raw})` : '#ffffff';
}

interface RasterOpts {
  /** 추가 배율 (devicePixelRatio 와 곱해짐). 기본 2 — 선명한 복사본. */
  scale?: number;
  /** 캔버스 배경색. 투명 PNG 가 흰 문서에서 안 보이는 문제 방지. 기본 흰색. */
  background?: string;
}

/** mermaid SVG 문자열을 PNG Blob 으로 래스터화. */
export function svgToPngBlob(svg: string, opts: RasterOpts = {}): Promise<Blob> {
  const { scale = 2, background = '#ffffff' } = opts;
  const normalized = normalizeMermaidSvg(svg);
  const size = readSvgSize(normalized) ?? { width: 1200, height: 800 };
  const ratio = (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1) * scale;

  return new Promise((resolve, reject) => {
    const blob = new Blob([normalized], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(size.width * ratio));
        canvas.height = Math.max(1, Math.round(size.height * ratio));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('canvas 2d context 없음'));
          return;
        }
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((out) => {
          if (out) resolve(out);
          else reject(new Error('toBlob 실패'));
        }, 'image/png');
      } catch (err) {
        reject(err instanceof Error ? err : new Error('래스터화 실패'));
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('SVG 이미지 로드 실패'));
    };
    img.src = url;
  });
}

/** mermaid SVG 를 PNG 이미지로 클립보드에 복사. 성공 true / 실패 false. */
export async function copySvgAsPng(svg: string, opts: RasterOpts = {}): Promise<boolean> {
  try {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return false;
    const blob = await svgToPngBlob(svg, opts);
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    return true;
  } catch {
    return false;
  }
}
